import { useState, useEffect, useRef } from "react";
import { Task, TaskStatus } from "@/types/task-management";
import TaskCard from "./TaskCard";
import TaskDialog from "./TaskDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Sun, Filter, SlidersHorizontal, X, Briefcase, Loader2 } from "lucide-react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { v4 as uuidv4 } from "uuid";
import confetti from 'canvas-confetti';

interface TaskKanbanViewProps {
  tasks: Task[];
  onTaskCreate: (task: Partial<Task>) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onTaskStatusChange: (taskId: string, status: TaskStatus) => void;
  onAddToMyDay?: (taskId: string, added: boolean) => void;
  onDeleteTask?: (taskId: string) => void;
  onPostponeTask?: (taskId: string) => void;
  isLoading?: boolean;
  onFilterByPlaybook?: (playbookId: string | null) => void;
}

// Definição das colunas do kanban com títulos e cores
const kanbanColumns: { 
  id: TaskStatus; 
  title: string;
  color: string;
  hoverColor: string;
  textColor: string;
}[] = [
  { 
    id: "todo", 
    title: "A fazer",
    color: "bg-blue-50",
    hoverColor: "bg-blue-100",
    textColor: "text-blue-700"
  },
  { 
    id: "in_progress", 
    title: "Em andamento",
    color: "bg-amber-50",
    hoverColor: "bg-amber-100",
    textColor: "text-amber-700"
  },
  { 
    id: "waiting", 
    title: "Aguardando",
    color: "bg-purple-50",
    hoverColor: "bg-purple-100",
    textColor: "text-purple-700"
  },
  { 
    id: "completed", 
    title: "Concluído",
    color: "bg-green-50",
    hoverColor: "bg-green-100",
    textColor: "text-green-700"
  }
];

// Estilo para o item quando está sendo arrastado
const getDraggedStyle = (isDragging: boolean, draggableStyle: any) => ({
  // Algumas propriedades básicas de estilo para tornar a experiência de arrastar mais suave
  userSelect: 'none',
  transition: isDragging ? 'transform 0.05s ease-out' : 'transform 0.2s ease-out',
  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
  boxShadow: isDragging ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
  zIndex: isDragging ? 1000 : 1,
  // Combinar com o estilo fornecido pelo draggable
  ...draggableStyle
});

// Estilo para o droppable quando um item está sendo arrastado sobre ele
const getDroppableStyle = (isDraggingOver: boolean, columnColor: string, columnHoverColor: string) => ({
  background: isDraggingOver ? columnHoverColor : columnColor,
  transition: 'background-color 0.3s ease',
});

export default function TaskKanbanView({ 
  tasks, 
  onTaskCreate, 
  onTaskUpdate,
  onTaskStatusChange,
  onAddToMyDay,
  onDeleteTask,
  onPostponeTask,
  isLoading = false,
  onFilterByPlaybook
}: TaskKanbanViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showMyDayOnly, setShowMyDayOnly] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [activePlaybookFilter, setActivePlaybookFilter] = useState<string | null>(null);
  const completedColumnRef = useRef<HTMLDivElement>(null);
  
  // Função para lançar confetti quando uma tarefa for concluída
  const triggerCompletionEffects = () => {
    if (!completedColumnRef.current) return;

    // Posição do elemento de coluna concluída
    const rect = completedColumnRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    
    // Lançar confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x, y },
      colors: ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800'],
      zIndex: 9999,
    });
    
    // Tocar som de conclusão (opcional)
    const audio = new Audio('/sounds/complete.mp3');
    audio.volume = 0.5;
    audio.play().catch(e => console.log('Som não reproduzido:', e));
  };

  // Agrupar tarefas por status
  const groupedTasks: Record<TaskStatus, Task[]> = {
    todo: [],
    in_progress: [],
    waiting: [],
    completed: [],
    cancelled: []  // Adicionando o status 'cancelled' que estava faltando
  };
  
  // Filtrar e agrupar tarefas
  tasks.forEach(task => {
    // Aplicar filtros
    const matchesSearch = 
      !searchQuery || 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
    const matchesMyDay = !showMyDayOnly || task.added_to_my_day;
    
    const matchesPriority = 
      priorityFilter.length === 0 || 
      priorityFilter.includes(task.priority);
      
    const matchesPlaybook = 
      !activePlaybookFilter || 
      task.playbook_id === activePlaybookFilter;
    
    if (matchesSearch && matchesMyDay && matchesPriority && matchesPlaybook && 
        kanbanColumns.some(col => col.id === task.status)) {
      groupedTasks[task.status as TaskStatus].push(task);
    }
  });
  
  // Extrair playbooks únicos para filtro
  const playbooks = Array.from(
    new Set(
      tasks
        .filter(task => task.playbook_id)
        .map(task => ({ id: task.playbook_id, name: task.playbook_name }))
    )
  ).filter(Boolean);
  
  // Criar nova tarefa em coluna específica
  const handleCreateTaskInColumn = (status: TaskStatus) => {
    // Usar um título inicial com base na coluna
    const initialData: Partial<Task> = { 
      status,
      // Se estiver com filtro de playbook ativo, definir o playbook
      ...(activePlaybookFilter && {
        playbook_id: activePlaybookFilter,
        playbook_name: playbooks.find(p => p.id === activePlaybookFilter)?.name
      })
    };
    
    // Abrir diálogo de criação com status pré-definido
    setSelectedTaskId('new');
    // Aqui você deve implementar a abertura do diálogo com os dados iniciais
  };
  
  // Manipular clique em tarefa para edição
  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
  };
  
  // Manipulador para adicionar ao meu dia
  const handleAddToMyDay = (taskId: string, added: boolean) => {
    if (onAddToMyDay) {
      onAddToMyDay(taskId, added);
    }
  };
  
  // Manipulador para excluir tarefa
  const handleDeleteTask = (taskId: string) => {
    if (onDeleteTask) {
      onDeleteTask(taskId);
    }
  };
  
  // Manipulador para postergar tarefa
  const handlePostponeTask = (taskId: string) => {
    if (onPostponeTask) {
      onPostponeTask(taskId);
    }
  };
  
  // Atualizar o filtro de playbook
  const handlePlaybookFilterChange = (playbookId: string | null) => {
    setActivePlaybookFilter(playbookId);
    
    if (onFilterByPlaybook) {
      onFilterByPlaybook(playbookId);
    }
  };
  
  // Contar tarefas visíveis
  const visibleTasksCount = Object.values(groupedTasks).flat().length;
  
  return (
    <div className="space-y-4">
      {/* Barra de ferramentas simplificada */}
      <div className="flex flex-wrap items-center gap-2 pb-2">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Procurar tarefas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        
        <Button 
          variant={showMyDayOnly ? "default" : "outline"} 
          size="sm"
          onClick={() => setShowMyDayOnly(!showMyDayOnly)}
          className="gap-1"
        >
          <Sun className={`h-4 w-4 ${showMyDayOnly ? "text-white" : "text-amber-500"}`} />
          <span className="hidden sm:inline">Meu dia</span>
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1">
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Prioridade</span>
              {priorityFilter.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5">
                  {priorityFilter.length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              checked={priorityFilter.includes("urgent")}
              onCheckedChange={(checked) => {
                setPriorityFilter(
                  checked
                    ? [...priorityFilter, "urgent"]
                    : priorityFilter.filter(p => p !== "urgent")
                );
              }}
            >
              <div className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-500"></span>
                Urgente
              </div>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={priorityFilter.includes("high")}
              onCheckedChange={(checked) => {
                setPriorityFilter(
                  checked
                    ? [...priorityFilter, "high"]
                    : priorityFilter.filter(p => p !== "high")
                );
              }}
            >
              <div className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-orange-500"></span>
                Alta
              </div>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={priorityFilter.includes("medium")}
              onCheckedChange={(checked) => {
                setPriorityFilter(
                  checked
                    ? [...priorityFilter, "medium"]
                    : priorityFilter.filter(p => p !== "medium")
                );
              }}
            >
              <div className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                Média
              </div>
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={priorityFilter.includes("low")}
              onCheckedChange={(checked) => {
                setPriorityFilter(
                  checked
                    ? [...priorityFilter, "low"]
                    : priorityFilter.filter(p => p !== "low")
                );
              }}
            >
              <div className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-500"></span>
                Baixa
              </div>
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setPriorityFilter([])}
              disabled={priorityFilter.length === 0}
            >
              Limpar filtros
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Filtro por Playbook (simplificado) */}
        {playbooks.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant={activePlaybookFilter ? "default" : "outline"} 
                size="sm"
                className="gap-1"
              >
                <Briefcase className="h-4 w-4" />
                <span className="hidden sm:inline">Playbook</span>
                {activePlaybookFilter && (
                  <Badge variant="secondary" className="ml-1 h-5">
                    1
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup 
                value={activePlaybookFilter || ""} 
                onValueChange={(value) => handlePlaybookFilterChange(value || null)}
              >
                <DropdownMenuRadioItem value="">
                  Todos os playbooks
                </DropdownMenuRadioItem>
                
                {playbooks.map(playbook => (
                  <DropdownMenuRadioItem 
                    key={playbook.id} 
                    value={playbook.id}
                  >
                    {playbook.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        
        {/* Botão para limpar filtros */}
        {(searchQuery || showMyDayOnly || priorityFilter.length > 0 || activePlaybookFilter) && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => {
              setSearchQuery("");
              setShowMyDayOnly(false);
              setPriorityFilter([]);
              handlePlaybookFilterChange(null);
            }}
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">Limpar filtros</span>
          </Button>
        )}
      </div>
      
      {/* Contador de tarefas e botão para limpar filtros */}
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm text-muted-foreground">
          Mostrando {visibleTasksCount} {visibleTasksCount === 1 ? 'tarefa' : 'tarefas'}
        </div>
      </div>
      
      {/* Indicador de carregamento */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary/70" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 overflow-x-auto">
          {kanbanColumns.map((column) => (
            <div 
              key={column.id} 
              className={`border rounded-lg shadow-sm flex flex-col h-full ${column.color}`}
              ref={column.id === "completed" ? completedColumnRef : undefined}
            >
              <div className={`p-2 border-b flex justify-between items-center sticky top-0 z-10 ${column.color} ${column.textColor}`}>
                <h3 className="font-extrabold flex items-center gap-2 text-sm">
                  {column.title}
                  <Badge variant="outline" className={column.textColor}>
                    {groupedTasks[column.id].length}
                  </Badge>
                </h3>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 hover:${column.textColor}`}
                  onClick={() => handleCreateTaskInColumn(column.id)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="flex-1 p-1.5 min-h-[180px] overflow-y-auto max-h-[calc(100vh-240px)]"
                    style={getDroppableStyle(snapshot.isDraggingOver, column.color, column.hoverColor)}
                  >
                    {groupedTasks[column.id].map((task, index) => (
                      <Draggable 
                        key={task.id} 
                        draggableId={task.id} 
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="mb-2 transition-all duration-200"
                            style={getDraggedStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style
                            )}
                          >
                            <TaskCard
                              task={task}
                              onClick={() => handleTaskClick(task.id)}
                              onAddToMyDay={(added) => handleAddToMyDay(task.id, added)}
                              onDelete={() => handleDeleteTask(task.id)}
                              onPostpone={() => handlePostponeTask(task.id)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {groupedTasks[column.id].length === 0 && (
                      <div className="flex items-center justify-center h-full min-h-[100px]">
                        <Button
                          variant="ghost"
                          className="flex flex-col items-center justify-center w-full h-full p-6 border border-dashed rounded-lg text-muted-foreground hover:text-foreground"
                          onClick={() => handleCreateTaskInColumn(column.id)}
                        >
                          <Plus className="h-6 w-6 mb-2" />
                          <span>Adicionar tarefa</span>
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      )}
      
      {/* Dialog para editar tarefa */}
      {selectedTaskId && (
        <TaskDialog
          task={selectedTaskId === 'new' ? undefined : tasks.find(t => t.id === selectedTaskId)}
          {...(selectedTaskId === 'new' ? {
            task: {
              id: 'new',
              title: '',
              status: 'todo',
              priority: 'medium',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              playbook_id: activePlaybookFilter || undefined,
              playbook_name: activePlaybookFilter 
                ? playbooks.find(p => p.id === activePlaybookFilter)?.name 
                : undefined
            } as Task
          } : {})}
          open={!!selectedTaskId}
          onOpenChange={(open) => {
            if (!open) setSelectedTaskId(null);
          }}
          onSubmit={(updates) => {
            if (selectedTaskId === 'new') {
              onTaskCreate(updates);
            } else {
              onTaskUpdate(selectedTaskId, updates);
            }
            setSelectedTaskId(null);
          }}
          onDelete={() => {
            if (onDeleteTask && selectedTaskId !== 'new') {
              onDeleteTask(selectedTaskId);
              setSelectedTaskId(null);
            }
          }}
        />
      )}
    </div>
  );
} 